// 🌗 THM — tema claro / oscuro. El mismo módulo que en 0101sanity.
//
// Estado: <html data-theme="dark|light">. Se guarda en localStorage bajo
// 'sty-theme' SÓLO cuando el usuario elige; sin elección guardada se sigue
// al sistema (prefers-color-scheme) y se cambia con él en caliente.
//
// El script inline del shell (index.html y AuthController::fallbackShell)
// hace exactamente esta misma lectura ANTES de la primera pintura, para que
// la página no parpadee del color contrario. Este módulo no decide el tema
// inicial: hereda el atributo que ya está puesto y, a partir de ahí,
// gobierna los cambios.
//
// El cambio se anima con la View Transitions API (un círculo que crece desde
// el botón que lo pidió) donde exista; sin ella, un fundido corto de colores;
// y con prefers-reduced-motion, ni lo uno ni lo otro: cambia y ya.
//
// API:
//   initTheme()              — una vez, en el boot (core🫀.js)
//   getTheme()               → 'dark' | 'light'
//   setTheme(t, {persist})   — aplica (y guarda, salvo persist:false)
//   toggleTheme(from?)       — alterna; `from` = {x,y} desde donde crece el círculo
// Evento: document → 'dk:theme' con detail.theme, tras cada cambio.

const KEY = 'sty-theme'
const SWIFT = 'cubic-bezier(.771,.029,.189,1.007)'
const FADE_MS = 350

export function getTheme() {
  return document.documentElement.dataset.theme === 'light' ? 'light' : 'dark'
}

function stored() {
  try {
    const v = localStorage.getItem(KEY)
    return v === 'light' || v === 'dark' ? v : null
  } catch { return null }
}

function apply(theme) {
  document.documentElement.dataset.theme = theme
  paintButtons(theme)
  document.dispatchEvent(new CustomEvent('dk:theme', { detail: { theme } }))
}

export function setTheme(theme, { persist = true, from = null } = {}) {
  const next = theme === 'light' ? 'light' : 'dark'
  if (persist) { try { localStorage.setItem(KEY, next) } catch { /* modo privado: sin memoria, sin drama */ } }
  if (getTheme() === next) { paintButtons(next); return }

  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  if (reduce) { apply(next); return }

  if (document.startViewTransition) {
    // Círculo que crece desde el botón (o desde la esquina del rail si el
    // cambio no vino de un click).
    const x = from?.x ?? 30
    const y = from?.y ?? window.innerHeight - 40
    const r = Math.hypot(Math.max(x, window.innerWidth - x), Math.max(y, window.innerHeight - y))
    const vt = document.startViewTransition(() => apply(next))
    vt.ready.then(() => {
      document.documentElement.animate(
        { clipPath: [`circle(0px at ${x}px ${y}px)`, `circle(${r}px at ${x}px ${y}px)`] },
        { duration: 520, easing: SWIFT, pseudoElement: '::view-transition-new(root)' },
      )
    }).catch(() => { /* la transición no arrancó: el tema ya está aplicado igualmente */ })
    return
  }

  // Sin View Transitions: fundido corto de colores (clase que activa
  // transiciones en todo, ver 🌗THM.pcss) y fuera.
  const root = document.documentElement
  root.classList.add('THM--fade')
  apply(next)
  setTimeout(() => root.classList.remove('THM--fade'), FADE_MS)
}

export function toggleTheme(from = null) {
  setTheme(getTheme() === 'light' ? 'dark' : 'light', { from })
}

export function initTheme() {
  paintButtons(getTheme())

  // Un solo listener delegado: vale para el botón del rail y para cualquier
  // otro [data-action="theme-toggle"] que aparezca.
  document.addEventListener('click', e => {
    const btn = e.target.closest('[data-action="theme-toggle"]')
    if (!btn) return
    const r = btn.getBoundingClientRect()
    toggleTheme({ x: r.left + r.width / 2, y: r.top + r.height / 2 })
  })

  // Sin elección guardada, el panel sigue al sistema también en caliente.
  const mq = window.matchMedia('(prefers-color-scheme: light)')
  mq.addEventListener('change', e => {
    if (stored()) return
    setTheme(e.matches ? 'light' : 'dark', { persist: false })
  })
}

// El botón dice a dónde LLEVA, no dónde estás. Sin title: lo explica la barra
// de ayuda (💬HLP) a través de data-tip, que aquí se mantiene al día.
function paintButtons(theme) {
  const light = theme === 'light'
  document.querySelectorAll('[data-action="theme-toggle"]').forEach(btn => {
    btn.setAttribute('aria-label', light ? 'Cambiar a tema oscuro' : 'Cambiar a tema claro')
    btn.dataset.tip = light ? 'Pasa al tema oscuro. Se recuerda en este navegador.' : 'Pasa al tema claro. Se recuerda en este navegador.'
    btn.dataset.key = 'tema'
    const label = btn.querySelector('.THM_label, .SBR_lbl')
    if (label) label.textContent = light ? 'tema oscuro' : 'tema claro'
  })
}
